import React from 'react';
import {
  OverviewSection, FootballSection, MatchAnalysisSection, PhysicalGpsSection,
  FitnessSection, BiometricsSection, AttendanceSection, MentalSection, PdpSection, InjurySection,
} from './sections';
import { EmptyState } from './shared';
import { PhysicalGpsAnalysisNotesView, SupportingEvidenceView } from './presentation/EvidenceViews';
import { PREVIEW_ORDER, SECTION_LABELS, fmtDate } from './constants';

const SECTION_COMPONENT = {
  football_performance: (s) => <FootballSection data={s.data} showMatches={false} />,
  physical_gps: (s) => <PhysicalGpsSection data={s.data} showMatches={false} />,
  fitness: (s) => <FitnessSection data={s.data} />,
  biometrics: (s) => <BiometricsSection data={s.data} />,
  attendance: (s) => <AttendanceSection data={s.data} />,
  mental: (s) => <MentalSection data={s.data} />,
  pdp: (s) => <PdpSection data={s.data} />,
  injury_rehab: (s) => <InjurySection data={s.data} />,
};

/**
 * ReportPreview — editorial, player/parent-facing rendering. Development-first
 * order (§50/§51). Renders ONLY selected sections. Used for the labelled Draft
 * Preview and for the frozen finalized snapshot view (same layout).
 */
const ReportPreview = ({ report, sections, draft = false, watermark = true }) => {
  const selected = new Set(report.selected_sections || []);
  const overview = sections?.overview?.data || null;
  const strengths = report.strengths || [];
  const priorities = report.priorities || [];
  const summary = report.development_summary || '';
  const includedIds = new Set((report.selected_evidence || []).map((e) => e.source_id));

  const Block = ({ id, children }) => (
    <div className="report-block" data-testid={`preview-section-${id}`}>
      <h3 className="mb-3 border-b-2 border-emerald-600/20 pb-1.5 text-xl font-semibold text-slate-800">{SECTION_LABELS[id]}</h3>
      {children}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      {watermark && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 border border-amber-200" data-testid="preview-draft-badge">
          {draft ? 'Draft Preview — not yet finalized' : 'Finalized Report'}
        </div>
      )}

      <div className="space-y-8 rounded-2xl bg-white p-5 sm:p-8 border border-slate-200">
        {/* Cover / Overview */}
        {selected.has('overview') && overview && (
          <div data-testid="preview-section-overview">
            <OverviewSection data={overview} meta={{ author: report.created_by_name }} />
          </div>
        )}

        {/* Development Summary */}
        {selected.has('development_summary') && (
          <Block id="development_summary">
            {summary ? <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-slate-700">{summary}</p>
              : <EmptyState message="No development summary written." />}
          </Block>
        )}

        {/* Key Strengths */}
        {selected.has('development_summary') && strengths.length > 0 && (
          <div data-testid="preview-strengths">
            <h3 className="mb-3 border-b-2 border-emerald-600/20 pb-1.5 text-xl font-semibold text-slate-800">Key Strengths</h3>
            <div className="space-y-3">
              {strengths.map((s, i) => (
                <div key={s.id || i} className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                  <div className="font-semibold text-slate-800">{s.title}</div>
                  {s.description && <p className="mt-1 text-sm text-slate-600">{s.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Priority Development Areas */}
        {selected.has('development_summary') && priorities.length > 0 && (
          <div data-testid="preview-priorities">
            <h3 className="mb-3 border-b-2 border-emerald-600/20 pb-1.5 text-xl font-semibold text-slate-800">Priority Development Areas</h3>
            <div className="space-y-3">
              {priorities.map((p, i) => (
                <div key={p.id || i} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-white">{i + 1}</span>
                    <span className="font-semibold text-slate-800">{p.title}</span>
                  </div>
                  <dl className="mt-2 space-y-1.5 text-sm">
                    {p.observation && <PriorityRow label="What We Observed" value={p.observation} />}
                    {p.needs_improvement && <PriorityRow label="Needs Improvement" value={p.needs_improvement} />}
                    {p.why_matters && <PriorityRow label="Why This Matters" value={p.why_matters} />}
                    {p.expected_standard && <PriorityRow label="Expected Standard" value={p.expected_standard} />}
                    {p.development_objective && <PriorityRow label="Development Objective" value={p.development_objective} />}
                    {p.player_action && <PriorityRow label="Player Action" value={p.player_action} />}
                    {p.pfap_action && <PriorityRow label="PFAP / Coach Action" value={p.pfap_action} />}
                    {p.recommended_action && !p.player_action && <PriorityRow label="Action" value={p.recommended_action} />}
                    {p.success_measure && <PriorityRow label="Success Measure" value={p.success_measure} />}
                    {p.review_date && <PriorityRow label="Review Date" value={fmtDate(p.review_date)} />}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data sections in development-first order, each respecting section_config */}
        {PREVIEW_ORDER.filter((k) => !['overview', 'development_summary'].includes(k)).map((k) => {
          if (!selected.has(k)) return null;
          const s = sections?.[k];
          const cfg = { include_in_report: true, show_hub_data: true, show_staff_assessment: true, ...((report.section_config || {})[k] || {}) };
          if (!cfg.include_in_report) return null;
          const hasHub = !!(s && s.allowed !== false && s.has_data && cfg.show_hub_data);
          const assessment = cfg.show_staff_assessment ? (report.staff_assessments?.[k] || '').trim() : '';
          const areas = k === 'football_performance' ? (report.football_areas || []) : [];
          const targets = (report.manual_targets || []).filter((t) => t.section_key === k);
          const sectionEvidence = (report.section_evidence || {})[k] || [];
          const gpsNotes = k === 'physical_gps' ? (report.physical_gps_analysis_notes || []) : [];
          if (!hasHub && !assessment && areas.length === 0 && targets.length === 0 && sectionEvidence.length === 0 && gpsNotes.length === 0) return null;
          const hub = hasHub ? (k === 'match_analysis'
            ? <MatchAnalysisSection data={s.data} included={includedIds} readOnly />
            : (SECTION_COMPONENT[k] ? SECTION_COMPONENT[k](s) : null)) : null;
          return (
            <Block key={k} id={k}>
              {hub}
              {targets.length > 0 && <ManualTargetsView targets={targets} />}
              {areas.length > 0 && <FootballAreasView areas={areas} />}
              {sectionEvidence.length > 0 && <SupportingEvidenceView items={sectionEvidence} testId={`preview-section-evidence-${k}`} />}
              {gpsNotes.length > 0 && <PhysicalGpsAnalysisNotesView notes={gpsNotes} evidence={sectionEvidence.filter((item) => item?.include_in_final !== false)} />}
              {assessment && <StaffAssessmentView text={assessment} />}
            </Block>
          );
        })}

        {/* Custom report sections */}
        {(report.custom_sections || []).filter((c) => c.include_in_report !== false && (c.title || c.body)).sort((a, b) => (a.order || 0) - (b.order || 0)).map((c, i) => (
          <div key={c.id || i} data-testid={`preview-custom-${i}`}>
            <h3 className="mb-3 border-b-2 border-emerald-600/20 pb-1.5 text-xl font-semibold text-slate-800">{c.title || 'Additional Notes'}</h3>
            {c.body && <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-slate-700">{c.body}</p>}
          </div>
        ))}
      </div>

      <p className="mt-4 text-center text-xs text-slate-400">
        {draft ? 'This is a draft preview. Data reflects current PFAP records and may change before finalization.'
          : `Finalized ${fmtDate(report.finalized_at)}${report.snapshot?.finalized_by_name ? ' by ' + report.snapshot.finalized_by_name : ''}. Pro Football Academy Portugal (PFAP) — Confidential.`}
      </p>
    </div>
  );
};

const StaffAssessmentView = ({ text }) => (
  <div className="mt-4 rounded-lg border-l-4 border-emerald-500/40 bg-slate-50 px-4 py-3" data-testid="preview-staff-assessment">
    {String(text).split(/\n{2,}/).map((p, i) => (
      <p key={i} className="text-[15px] leading-relaxed text-slate-700 [&:not(:first-child)]:mt-2 whitespace-pre-wrap">{p}</p>
    ))}
  </div>
);

const ASSESS_LABEL = {
  strength: 'Strength', on_track: 'On Track', developing: 'Developing',
  priority_development_area: 'Priority Development Area', not_assessed: 'Not Assessed',
};

const FootballAreasView = ({ areas }) => (
  <div className="mt-4 space-y-3" data-testid="preview-football-areas">
    {[...areas].sort((a, b) => (a.order || 0) - (b.order || 0)).map((a, i) => (
      <div key={a.id || i} className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-slate-800">{a.title || 'Assessment Area'}</span>
          {a.current_assessment && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{ASSESS_LABEL[a.current_assessment] || a.current_assessment}</span>}
        </div>
        <dl className="mt-2 space-y-1.5 text-sm">
          {a.observation && <PriorityRow label="What We Observed" value={a.observation} />}
          {a.doing_well && <PriorityRow label="Doing Well" value={a.doing_well} />}
          {a.needs_improvement && <PriorityRow label="Needs Improvement" value={a.needs_improvement} />}
          {a.why_matters && <PriorityRow label="Why This Matters" value={a.why_matters} />}
          {a.expected_standard && <PriorityRow label="Expected Standard" value={a.expected_standard} />}
          {a.development_focus && <PriorityRow label="Development Focus" value={a.development_focus} />}
        </dl>
      </div>
    ))}
  </div>
);

const ManualTargetsView = ({ targets }) => (
  <div className="mt-4 space-y-2" data-testid="preview-manual-targets">
    {[...targets].sort((a, b) => (a.order || 0) - (b.order || 0)).map((t, i) => (
      <div key={t.id || i} className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="font-medium text-slate-800">{t.label || 'Target'}</span>
          <span className="text-sm text-slate-600">
            {t.current_value ? <>Current <strong>{t.current_value}{t.unit ? ` ${t.unit}` : ''}</strong></> : null}
            {t.target_value ? <span className="ml-2">Target <strong>{t.target_value}{t.unit ? ` ${t.unit}` : ''}</strong></span> : null}
          </span>
        </div>
        {t.context && <div className="mt-0.5 text-xs text-slate-500">{t.context}</div>}
        {t.explanation && <div className="mt-0.5 text-sm text-slate-600">{t.explanation}</div>}
        <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">PFAP Staff Target — not a validated benchmark</div>
      </div>
    ))}
  </div>
);

const PriorityRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:gap-2">
    <dt className="w-40 shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
    <dd className="text-slate-700">{value}</dd>
  </div>
);

export default ReportPreview;
